import PropTypes from 'prop-types';
import React, {Component} from 'react';

import {postRequest} from '../../../utils/request';

import LeaveWarning from '../../../components/LeaveWarning';

import Lesson from './Lesson';
import LessonError from './LessonError';
import LessonSpinner from './LessonSpinner';
import LessonComplete, {redirect} from './LessonComplete';

const Source = window.SourceApp || 'Desktop';

/**
 * Handles saving the answers on completion.
 */
class LessonForm extends Component {
    static propTypes = {
        CSRFMiddlewareToken: PropTypes.string.isRequired,
        completionURL: PropTypes.string.isRequired,
        cancelationURL: PropTypes.string.isRequired,
        LessonComplete: PropTypes.func,
        LessonSpinner: PropTypes.func,
        LessonError: PropTypes.func,
        Lesson: PropTypes.func,
        onCompleted: PropTypes.func,
        onLeaveConfirm: PropTypes.func,
    };

    static defaultProps = {
        onLeaveConfirm: undefined,
        onCompleted: null,
        LessonComplete,
        LessonSpinner,
        LessonError,
        Lesson,
    };

    /**
     * Set the initial state.
     */
    constructor(props) {
        super(props);

        this.state = {
            answers: {},
            completed: false,
            request: null,
            error: null,
            showAbortModal: false,
        };
        this.retrying = false;
        this.onComplete = this.onComplete.bind(this);
    }

    /**
     * Renders the appropriate sub-component for the current state.
     */
    render() {
        const {answers, completed, request, error} = this.state;
        const {
            LessonComplete,
            LessonSpinner,
            LessonError,
            Lesson,
            onCompleted,
            onLeaveConfirm,
            ...props
        } = this.props;

        if (completed) return <LessonComplete onCompleted={onCompleted} />;
        else if (request) return <LessonSpinner />;
        else if (error) return <LessonError error={error} onRetry={() => {
            window.Sentry.addBreadcrumbInfo("lesson", `Retrying current lesson result`);
            this.retrying = true;
            this.onComplete(answers)} 
        }/>;

        return (
            <LeaveWarning onConfirm={onLeaveConfirm}>
                <Lesson {...props} onComplete={this.onComplete} onAbort={this.handleAbort} />
            </LeaveWarning>
        );
    }

    /**
     * Start measuring the completion time on initial mount.
     */
    componentDidMount() {
        this.start = new Date();
        this.end = null;
    }

    /**
     * Handles the completion of the lesson.
     * Sends the answers to the backend.
     */
    onComplete(answers) {
        this.end = this.end || new Date();

        const {CSRFMiddlewareToken, completionURL} = this.props;

        const request = postRequest(
            this.getData(answers),
            completionURL,
            CSRFMiddlewareToken,
            code => code === 201,
            () => {},
            () => {
                this.retrying = false;
                this.setState({
                    request: null,
                    completed: true,
                });
            },
            error => {
                window.Sentry.addBreadcrumbInfo("lesson", `Error occured during lesson filling out the lesson, error: ${JSON.stringify(error)}`);
                if (this.retrying) {
                    window.Sentry.exception(new AfterRetryLessonException("Error occured during retry after POST lesson result"));
                } else {
                    window.Sentry.exception(new LessonException("Error occured during POST lesson result"));
                }
                
                this.setState({
                    request: null,
                    error,
                });
            },
        );

        return this.setState({
            answers,
            request,
        });
    }

    handleAbort = (answers, abort_reason) => {
        const {CSRFMiddlewareToken, cancelationURL, onLeaveConfirm} = this.props;

        this.end = new Date();

        const data = {
            ...this.getData(answers),
            abort_reason,
        };

        const request = postRequest(
            data,
            cancelationURL,
            CSRFMiddlewareToken,
            code => code === 201,
            () => {},
            () => {
                if (onLeaveConfirm) onLeaveConfirm(abort_reason, 'dialog');
                return redirect('home');
            },
            error => this.setState({
                request: null,
                error,
            }),
        );

        return this.setState({
            answers,
            request,
        });
    };

    getData(answers, source = Source) {
        return {
            answers: JSON.stringify(answers),
            start: this.start.toISOString(),
            end: this.end.toISOString(),
            source,
        };
    }

}

export default LessonForm;

class LessonException extends Error {
    constructor(msg) {
        super(msg)
    }
}

class AfterRetryLessonException extends Error {
    constructor(msg) {
        super(msg)
    }
}




// WEBPACK FOOTER //
// ./scripts/frontend/lessons/components/LessonForm.js